import React, { useState, useContext } from 'react';
import PropTypes from 'prop-types';
import _ from 'lodash';
import appConfig from 'app-config';
import { ViewModelForm, ViewModelServiceContext, useDataRefresh } from '@xengage/gw-portals-viewmodel-react';
import { MLDriverVehicleUtil, MLNotificationBlockUtil} from 'ml-portals-util-react';
import { useTranslator } from '@jutro/locale';

import messages from './MLVehicleOperatorStatusComponent.messages';
import metadata from './MLVehicleOperatorStatusComponent.metadata.json5';
import styles from './MLVehicleOperatorStatusComponent.module.scss';

const MLVehicleOperatorStatusComponent = (props) => {
    const {
        vehicleOperator, 
        label,
        driverType,
        canExcludeDrivers,
        isRemove,
        isPolicyChange,
        secondaryNamedInsured,
        onValidate,
        showValidationErrors,
        setIsComponentErrorPresent,
        isModernProduct,
        isUMB_NP
    } = props;

    const isAMP = appConfig.portalCode === 'AMP';
    const viewModelService = useContext(ViewModelServiceContext);
    const translator = useTranslator();
    const { refreshData } = useDataRefresh();
    const driverTypeList = viewModelService.productMetadata.get('pc').types.getTypelist('DriverStatusType_ML');
    const discovered = driverTypeList.getCode('discovered').code;
    const [removalAlert, setRemovalAlert] = useState('');

    const { clearBlockAlerts, getNumberOfAlerts } = MLNotificationBlockUtil();

    const {
        isSamePerson,
        getDriverNotListedReasonsForAdding
    } = MLDriverVehicleUtil();

    const mapTypeCodesToAvailableValues = (driverTypeCodes) => {
        return driverTypeCodes.map((option) => {
            return {
                code: option.code,
                name: translator({
                    id: option.name,
                    defaultMessage: option.name
                })
            };
        });
    };

    const getFilteredDriverTypeOptions = (excludedCode , unwanted) => {
        let driverTypeCodes = driverTypeList.codes.filter((val) => val.code !== discovered)
        if (excludedCode) {
            driverTypeCodes = driverTypeCodes.filter((type) => type.code !== excludedCode && type.code !== unwanted);
        }
        return mapTypeCodesToAvailableValues(driverTypeCodes);
    };

    const getAddingOptions = () => {
        const codes = driverTypeList.codes.filter((status) => _.includes(getDriverNotListedReasonsForAdding(driverType, isModernProduct, isUMB_NP), status.code));

        return mapTypeCodesToAvailableValues(codes);
    };

    const getRemovingOptions = () => {
        return driverType === 'rated' ? getFilteredDriverTypeOptions('insure_driver','unwanted') : getFilteredDriverTypeOptions();
    };

    const addingOptions = getAddingOptions();
    const removingOptions = getRemovingOptions();

    const isExcludedReason = (removalReason) => {
        return removalReason === 'unwanted' || removalReason === 'license_revoked';
    };

    const isSecondaryNamedInsured = () => {
        if (!_.isUndefined(secondaryNamedInsured)) {
            return isSamePerson(vehicleOperator.contact.value, secondaryNamedInsured.value);
        }
    };

    const isStateND = () => vehicleOperator.value.contact.address.state === 'ND';
    const isStateMD = () => vehicleOperator.value.contact.address.state === 'MD';
    const isStateCO = () => vehicleOperator.value.contact.address.state === 'CO';
    
    const validateRemovalReason = () => {
        let alertToReturn;
        const removalReason = vehicleOperator.value.driverStatusForRemoving;
        const isSpouse = vehicleOperator.relationshipToInsured.value.code === 'spouse';
        const isNamedInsured = vehicleOperator.relationshipToInsured.value.code === 'self';
        if (!canExcludeDrivers && isExcludedReason(removalReason)) {
            alertToReturn = translator(messages.notValidReasonToRemoveDriver);
        } else if (isSpouse && !isStateCO() && ((!(isStateND() || isStateMD()) && (removalReason !== 'never_licensed' && removalReason !== 'insure_driver')) || ((isStateND() || isStateMD()) && removalReason !== 'license_revoked' && removalReason !== 'unwanted' && removalReason !== 'never_licensed' && removalReason !== 'insure_driver'))) {
            alertToReturn = translator(messages.spouseCannotBeRemovedIfLicensed);
        } else if (!(isStateND() || isStateMD()) && !isPolicyChange && isExcludedReason(removalReason) && isNamedInsured) {
            alertToReturn = translator(messages.notValidReasonToRemoveNamedInsured);
        } else if (!isPolicyChange && isExcludedReason(removalReason) && isSecondaryNamedInsured() && !isStateCO()) {
            alertToReturn = translator(messages.notValidReasonToRemoveSecondaryNamedInsured);
        } else if (driverType === 'household') {
            if (removalReason === 'own_insurance'
                || removalReason === 'never_licensed'
                || removalReason === 'abroad'
                || removalReason === 'mission'
                || removalReason === 'permit') {
                alertToReturn = translator(messages.notValidReasonToRemoveHouseholdMember);
            }
        }
        return alertToReturn;
    };

    const updateDriverVM = () => {
        if (isRemove) {
            clearBlockAlerts('vehicleOperatorErrorMessage');
            const alert = validateRemovalReason();
            setRemovalAlert(alert);
            // US985436 -> if there is an error in child component, set the error state
            if(!_.isEmpty(alert)){
                setIsComponentErrorPresent(true);
            }
            else{
                setIsComponentErrorPresent(false);
            }
        }
        refreshData();
    };

    const overrideProps = {
        removeAlertContainer: {
            visible: isRemove && getNumberOfAlerts('vehicleOperatorErrorMessage') < 1,
        },
        vehicleOperatorNotification: {
            visible: !_.isEmpty(removalAlert),
            message: removalAlert
        },
        driverStatusForAdding: {
            className: isAMP ? 'col-md-6' : 'col-md-4',
            visible: driverType !== 'rated' && !isRemove
        },
        addStatus: {
            label: label,
            availableValues: addingOptions
        },
        driverStatusForRemoving: {
            className: isAMP ? 'col-md-6' : 'col-md-4',
            visible: isRemove          
        },
        removeStatus: {
            label: label,
            availableValues: removingOptions
        }        
    };

    const resolvers = {
        resolveClassNameMap: styles
    };
    
    return (
        <ViewModelForm
            uiProps={metadata.componentContent}
            model={vehicleOperator}
            overrideProps={overrideProps}
            classNameMap={resolvers.resolveClassNameMap}
            onModelChange={updateDriverVM}
            onValidationChange={onValidate}
            showErrors={showValidationErrors}
        />
    );
};

MLVehicleOperatorStatusComponent.propTypes = {   
    vehicleOperator: PropTypes.object, 
    label: PropTypes.string,
    driverType: PropTypes.string,
    canExcludeDrivers: PropTypes.bool,
    isRemove: PropTypes.bool,
    isPolicyChange: PropTypes.bool,
    secondaryNamedInsured: PropTypes.object, 
    onValidate: PropTypes.func,
    showValidationErrors: PropTypes.bool,
    setIsComponentErrorPresent: PropTypes.func
}

MLVehicleOperatorStatusComponent.defaultProps = {
    isRemove: false,
    showValidationErrors: false
}

export default MLVehicleOperatorStatusComponent;
